import { useMutation } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import { useState } from 'react';

import { postUserLangApi } from '@flipster/apis/v1/user';
import { useAuth } from '@flipster/features/src/shared/auth/state';
import { languageLabels, supportedLocaleToLanguageTag } from '@flipster/i18n/constants';
import { useSupportedLocale } from '@flipster/i18n/hooks';
import { usePathname } from '@flipster/i18n/routing';
import { SupportedLocale } from '@flipster/i18n/types';
import { useOverlayItem } from '@flipster/overlay';
import { Badge, Button, RadioButton } from '@flipster/react/design-system';
import { Modal } from '@flipster/react/design-system-legacy/modal';
import { snackbar } from '@flipster/react/design-system/snackbar';
import { css } from '@flipster/styled-system/css';
import { hstack } from '@flipster/styled-system/patterns';
import { getErrorMessage } from '@flipster/utils/error';

export const LanguageModal = () => {
  const transCommon = useTranslations('Common.r250401');
  const transLanguageSetting = useTranslations('Language_setting.r250401');
  const pathname = usePathname();
  const locale = useSupportedLocale();

  const [language, setLanguage] = useState<SupportedLocale>(locale);

  const { onClose } = useOverlayItem();
  const { isLoggedIn } = useAuth();

  const { mutateAsync } = useMutation({
    mutationKey: ['postUserLangApi'],
    mutationFn: postUserLangApi,
  });

  const onConfirm = async () => {
    try {
      if (isLoggedIn) {
        await mutateAsync(supportedLocaleToLanguageTag[language]);
      }
      // 새로고침 하지 않으면 언어 변경이 제대로 적용되지 않는 이슈가 있음
      window.location.replace(`/${language}/${pathname}`);
    } catch (error) {
      snackbar.error({ message: getErrorMessage(error) });
    } finally {
      onClose();
    }
  };

  return (
    <Modal size='small'>
      <Modal.Close onClose={onClose} />
      <Modal.Header>
        <Modal.Title label={transLanguageSetting('Language_setting_title')} />
      </Modal.Header>
      <Modal.Content>
        <p className={css({ textStyle: 'Text/M/Regular', mb: 12 })}>
          English is the primary language of Flipster. Translations in other languages may be incomplete or inaccurate.
        </p>
        <Message language={language} setLanguage={setLanguage} />
      </Modal.Content>
      <Modal.Footer>
        <Button onClick={onConfirm} label={transCommon('Confirm')} />
      </Modal.Footer>
    </Modal>
  );
};

const languageOptions = [
  {
    label: languageLabels.en,
    value: 'en',
    isBeta: false,
  },
  {
    label: languageLabels.vi,
    value: 'vi',
    isBeta: true,
    description:
      'Tiếng Anh là ngôn ngữ chính của Flipster. Các bản dịch sang ngôn ngữ khác có thể không đầy đủ hoặc chính xác.',
  },
  {
    label: languageLabels['zh-CN'],
    value: 'zh-CN',
    isBeta: true,
    description: '英语是Flipster的主要语言。其他语言的翻译可能不完整或不准确。',
  },
  {
    label: languageLabels['zh-TW'],
    value: 'zh-TW',
    isBeta: true,
    description: 'Flipster 的主要語言為英文。其他語言的翻譯可能不完整或不夠準確。',
  },
  {
    label: languageLabels.ru,
    value: 'ru',
    isBeta: true,
    description:
      'Основным языком Flipster является английский. Переводы на другие языки могут быть неполными или неточными.',
  },
];

const Message = ({
  language,
  setLanguage,
}: {
  language: SupportedLocale;
  setLanguage: (language: SupportedLocale) => void;
}) => {
  return (
    <fieldset className={css({ display: 'flex', flexDirection: 'column' })}>
      {languageOptions.map((item) => (
        <div key={item.value}>
          <RadioButton
            key={item.value}
            name='language'
            value={item.value}
            checked={language === item.value}
            onChange={(e) => setLanguage(e.target.value as SupportedLocale)}
            css={{ color: 'content.default.level-4', py: 12 }}
          >
            <span className={hstack({ gap: 8 })}>
              {item.label}
              {item.isBeta && <Badge type='tint'>Beta</Badge>}
            </span>
          </RadioButton>
          {language === item.value && <p className={css({ textStyle: 'Text/M/Regular' })}>{item.description}</p>}
        </div>
      ))}
    </fieldset>
  );
};
